import React, { useState, useEffect, MouseEventHandler, useMemo } from "react";
import H3 from "../../components/Typography/H3";
import Button from "../../components/Button";
import TextField from "../../components/TextField";
import { useFetchApi } from "../../hooks/useFetchApi";
import Loader from "../../components/Loader";
import H4 from "../../components/Typography/H4";
import Badge from "../../components/Badge";
import AddEditModal from "./AddEditModal";
import { captialCase } from "../../helper";
import DeleteModal from "./DeleteModal";
import { Table } from "../../components/table";
import type { ColumnsType } from "antd/es/table";

// Define the Metric type
interface Metric {
  id: number;
  name: string;
  metric_category_name: string;
  parent_metric_name: string | null;
  status: boolean;
}

const MetricsManagement: React.FC = (): React.ReactElement => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedData, setEditData] = useState<any>({});
  const [openModal, toggleModal] = useState<any>({
    edit: false,
    new: false,
    delete: false,
  });
  const {
    execute,
    loading,
    response: metrics,
  } = useFetchApi("/api/metrics?fullResponse=true", {}, "GET");

  const toggle = (e: any, metric?: any) => {
    const type = e.target.id;

    if (metric && Object.keys(metric).length) {
      setEditData(metric);
    }

    if (type) {
      toggleModal({ ...openModal, [type]: !openModal[type] });
    }

    if (openModal[type]) {
      setEditData({});
    }
  };

  const filteredMetrics = useMemo(() => {
    if (!metrics || !searchTerm.trim()) {
      return metrics || [];
    }
    return metrics.filter(
      (metric: Metric) =>
        metric.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        metric.metric_category_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    );
  }, [metrics, searchTerm]);

  const clearSearch = () => {
    setSearchTerm("");
  };

  const columns: ColumnsType<Metric> = [
    {
      title: "ID #",
      dataIndex: "id",
      key: "id",
      align: "left",
      width: 80,
    },
    {
      title: "Metric Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      render: (name: string) => captialCase(name),
    },
    {
      title: "Metric Category",
      dataIndex: "metric_category_name",
      key: "metric_category_name",
      align: "left",
      render: (category: string) => captialCase(category),
    },
    {
      title: "Parent Metric",
      dataIndex: "parent_metric_name",
      key: "parent_metric_name",
      align: "left",
      render: (name: string | null) =>
        name ? (
          captialCase(name)
        ) : (
          <span className="text-gray-400"> None </span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "left",
      render: (status: boolean) => (
        <Badge status={status ? "success" : "error"}>
          {status ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 300,
      render: (_, metric: Metric) => (
        <div className="flex gap-2 justify-center">
          <Button
            fontSize="small"
            variant="primary"
            id="edit"
            onClick={(e: MouseEventHandler<HTMLButtonElement>) =>
              toggle(e, metric)
            }
          >
            Edit
          </Button>
          <Button
            fontSize="small"
            variant="error"
            id="delete"
            onClick={(e: MouseEventHandler<HTMLButtonElement>) =>
              toggle(e, metric)
            }
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    execute();
  }, []);

  return (
    <div className="flex flex-col w-full p-8 bg-white min-h-full">
      <Loader show={loading} />
      <AddEditModal
        data={openModal.edit ? selectedData : {}}
        status={openModal.new || openModal.edit}
        onClose={toggle}
        onSuccess={execute}
        metrics={metrics || []}
      />
      <DeleteModal
        id="delete"
        data={selectedData}
        status={openModal.delete}
        onClose={toggle}
        onSuccess={execute}
      />

      <div className="flex justify-between mb-8 text-white items-center">
        <H4>Metrics</H4>
        <Button id="new" onClick={toggle} variant="primary">
          Add New Metrics
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search metric name or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear
          </button>
        )}
        {searchTerm && (
          <span className="text-sm text-gray-600">
            Found {filteredMetrics.length} of {metrics?.length || 0}
          </span>
        )}
      </div>

      <div className="relative z-0">
        <Table<Metric>
          columns={columns}
          dataSource={filteredMetrics}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowHoverable={true}
          bordered={true}
          size="middle"
          nullText="No data available. Add new"
          className="shadow-sm"
        />
      </div>
    </div>
  );
};

export default MetricsManagement;
